import Link from 'next/link';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import AgentFaq from '@/components/agents/AgentFaq';
import AgentFinalCta from '@/components/agents/AgentFinalCta';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import HubMatrix from './HubMatrix';
import {
  breadcrumbJsonLd,
  CheckedNote,
  JsonLd,
  LAST_CHECKED,
  LAST_CHECKED_ISO,
  SITE,
} from './kit';
import type { AlternativesHubProps } from './types';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  SectionHeading,
  Stage,
  tone,
  useOpenAgentDemo,
} from '../primitives';
import { useReveal } from '../useReveal';

const TITLE = 'Compare AI Chatbot Builders: Chatbase, Intercom, Tidio & More';
const DESCRIPTION =
  'Sourced comparisons of Easy-Peasy.AI with Chatbase, Intercom, Tidio, Botpress, tawk.to, Voiceflow and Drift: prices, channels and when each fits better.';

/** The comparisons hub (English only). */
export default function AlternativesHub({
  cards,
  matrix,
}: AlternativesHubProps) {
  const openDemo = useOpenAgentDemo();
  const names = cards.map((item) => item.name);
  const list = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  const faqs = [
    {
      question: 'How do you compare these tools?',
      answer: `We read each product’s own pricing page, documentation and help center, and link every fact to the page it came from. The facts were last checked in ${LAST_CHECKED}. Our own side comes from our product and docs.`,
    },
    {
      question: 'Is Easy-Peasy.AI always the better choice?',
      answer:
        'No. It is an AI agent that answers from your content on your website, messaging apps, email and the phone. If your team chats with customers in a shared inbox, a helpdesk or live chat tool fits better, and each comparison says when.',
    },
    {
      question: 'Does Easy-Peasy.AI have live chat for my team?',
      answer:
        'No. The agent answers every message. It can collect contact details, email your team or book a meeting when someone needs a person.',
    },
    {
      question: 'Can I try Easy-Peasy.AI for free?',
      answer:
        'Yes. The Free plan includes 1 agent and 100 messages every 30 days on your website, Telegram, Messenger and Slack, with no credit card needed.',
    },
  ];

  return (
    <main className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo
        title={TITLE}
        description={DESCRIPTION}
        image={`${SITE}/api/og-template?title=${encodeURIComponent(
          'Compare AI chatbot builders',
        )}`}
        structuredData={false}
      />
      <JsonLd
        data={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'AI Agents', path: '/chatbots' },
            { name: 'Comparisons', path: '/alternatives' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: TITLE,
            description: DESCRIPTION,
            url: `${SITE}/alternatives`,
            dateModified: LAST_CHECKED_ISO,
            inLanguage: 'en',
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: cards.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: `Easy-Peasy.AI vs ${item.name}`,
                url: `${SITE}/alternatives/${item.slug}`,
              })),
            },
          },
        ]}
      />

      <header className='relative'>
        <LandingPageNavigation />
        <div className='mx-auto grid max-w-7xl gap-x-12 gap-y-14 px-6 pb-4 pt-10 sm:pt-14 lg:grid-cols-12 lg:items-center lg:px-8'>
          <div className='min-w-0 lg:col-span-7'>
            <nav aria-label='Breadcrumb' className={styles.enter}>
              <ol
                className={clsxm(
                  'inline-flex max-w-full items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                  tone.quiet,
                )}
              >
                <li>
                  <Link
                    href='/chatbots'
                    className='transition-colors hover:text-primary-700 dark:hover:text-primary-300'
                  >
                    AI Agents
                  </Link>
                </li>
                <li aria-hidden='true' className='opacity-50'>
                  /
                </li>
                <li aria-current='page' className={tone.ink}>
                  Comparisons
                </li>
              </ol>
            </nav>
            <h1
              className={clsxm(
                styles.enter,
                'mt-8 flex items-center gap-3 text-[0.9375rem] font-semibold leading-6 text-primary-700 dark:text-primary-300',
              )}
              style={delay(30)}
            >
              <span
                aria-hidden='true'
                className='h-px w-8 flex-none bg-current opacity-50'
              />
              AI chatbot builder comparisons
            </h1>
            <p
              className={clsxm(
                styles.display,
                styles.enter,
                'mt-5 text-[2.5rem] font-semibold leading-[1.04] sm:text-[3.25rem] sm:leading-[1.02] xl:text-[3.75rem]',
                tone.ink,
              )}
              style={delay(60)}
            >
              How <span className='whitespace-nowrap'>Easy-Peasy.AI</span>{' '}
              compares.{' '}
              <span className='text-primary-600 dark:text-primary-400'>
                Facts from each company’s own pages.
              </span>
            </p>
            <p
              className={clsxm(
                styles.enter,
                'mt-6 max-w-xl text-lg leading-8 sm:text-xl sm:leading-[1.6]',
                tone.body,
              )}
              style={delay(140)}
            >
              Side-by-side comparisons with {list}: what each one costs, where
              it answers your customers, and when it’s the better choice.
            </p>
            <div
              className={clsxm(styles.enter, 'mt-8 flex flex-wrap gap-3')}
              style={delay(200)}
            >
              <Link href='/login?preset=bots' className={buttonPrimary}>
                Build your agent free
                <ArrowGlyph />
              </Link>
              <button
                type='button'
                onClick={openDemo}
                className={buttonSecondary}
              >
                <span className='relative flex h-2 w-2'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                  <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
                </span>
                Talk to our agent
              </button>
            </div>
            <CheckedNote className={clsxm(styles.enter, 'mt-6')} />
          </div>

          <div className='-mx-3 min-w-0 sm:mx-0 lg:col-span-5'>
            <Stage
              variant='indigo'
              halftoneAt='100% 0%'
              className={clsxm(styles.enter, 'px-4 py-8 sm:px-10 sm:py-12')}
              style={delay(260)}
            >
              <IndexCard cards={cards} />
            </Stage>
          </div>
        </div>
      </header>

      <ComparisonCards cards={cards} />
      <HubMatrix matrix={matrix} />
      <Method />
      <AgentFaq faqs={faqs} />
      <AgentFinalCta
        title='Try it free next to the tool you use today.'
        exchange={{
          question: 'Can you answer from our website?',
          answer: 'Yes. Give me your address and I’m trained in minutes.',
        }}
      />
      <Footer />
    </main>
  );
}

/** The hero's list of comparisons, as a card on the stage. */
function IndexCard({ cards }: Pick<AlternativesHubProps, 'cards'>) {
  return (
    <div
      className={clsxm(
        'mx-auto max-w-[26rem] overflow-hidden rounded-[22px] shadow-[0_40px_90px_-40px_rgba(20,10,80,0.75)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center justify-between gap-3 border-b border-[#0f0f28]/[0.07] px-5 py-4 dark:border-white/[0.08]'>
        <span className={clsxm('text-[15px] font-semibold', tone.ink)}>
          Comparisons
        </span>
        <span className={clsxm('text-xs', tone.quiet)}>
          Checked {LAST_CHECKED}
        </span>
      </div>
      <ul>
        {cards.map((item) => (
          <li
            key={item.slug}
            className='border-b border-[#0f0f28]/[0.06] last:border-b-0 dark:border-white/[0.07]'
          >
            <Link
              href={`/alternatives/${item.slug}`}
              className='group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-[#f7f7fa] dark:hover:bg-white/[0.04]'
            >
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-[15px] font-semibold',
                    tone.ink,
                  )}
                >
                  vs {item.name}
                </span>
                <span className={clsxm('block truncate text-xs', tone.quiet)}>
                  {item.category}
                </span>
              </span>
              {item.badge && (
                <span className='flex-none rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'>
                  {item.badge}
                </span>
              )}
              <ArrowGlyph
                className={clsxm(
                  'h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5',
                  tone.quiet,
                )}
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ComparisonCards({ cards }: Pick<AlternativesHubProps, 'cards'>) {
  const { ref, state } = useReveal<HTMLUListElement>(0.15);
  return (
    <section id='comparisons' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='Pick the tool you’re comparing.'
          tail='Each page has a full table, prices, sources and a switching guide.'
        />
        <ul
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
          )}
        >
          {cards.map((item, index) => (
            <li key={item.slug} className={styles.rv} style={delay(index * 60)}>
              <Link
                href={`/alternatives/${item.slug}`}
                className={clsxm(
                  'group flex h-full flex-col rounded-[24px] p-6 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-30px_rgba(30,20,90,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:p-7',
                  tone.surface,
                  tone.line,
                )}
              >
                <span className='flex items-start justify-between gap-3'>
                  <span>
                    <span
                      className={clsxm(
                        'block text-xs font-semibold uppercase tracking-[0.08em]',
                        tone.quiet,
                      )}
                    >
                      {item.category}
                    </span>
                    <span
                      className={clsxm(
                        'mt-2 block text-2xl font-semibold tracking-tight',
                        tone.ink,
                      )}
                    >
                      <span className='whitespace-nowrap'>Easy-Peasy.AI</span>{' '}
                      vs {item.name}
                    </span>
                  </span>
                  {item.badge && (
                    <span className='mt-0.5 flex-none rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'>
                      {item.badge}
                    </span>
                  )}
                </span>
                <span
                  className={clsxm(
                    'mt-4 flex-1 text-[15px] leading-7',
                    tone.body,
                  )}
                >
                  {item.summary}
                </span>
                <span
                  className={clsxm(
                    'mt-5 block rounded-xl bg-[#f7f7fa] px-4 py-3 text-sm dark:bg-white/[0.04]',
                    tone.body,
                  )}
                >
                  <span className={clsxm('font-semibold', tone.ink)}>
                    {item.badge ? 'Was best for: ' : 'Best for: '}
                  </span>
                  {item.bestFor}
                </span>
                <span className='mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 dark:text-primary-300'>
                  {item.name} alternative
                  <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5' />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Method() {
  const points = [
    {
      title: 'Official sources only',
      body: 'Prices, plans and features come from each company’s own pricing page, docs or help center, never from other review sites.',
    },
    {
      title: 'Dated and linked',
      body: `Every fact links to its source, and every page says when it was last checked: ${LAST_CHECKED}.`,
    },
    {
      title: 'Fair about the trade-offs',
      body: 'Each comparison says when the other tool is the better choice, including the things we don’t do, like live chat for your team.',
    },
  ];
  return (
    <section id='method' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='How we compare.'
          tail='Three rules for every page.'
        />
        <ul className='mt-12 grid gap-4 md:grid-cols-3'>
          {points.map((point, index) => (
            <li
              key={point.title}
              className={clsxm('rounded-[24px] p-7', tone.surface, tone.line)}
            >
              <span className='flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700 dark:bg-primary-400/10 dark:text-primary-300'>
                {index + 1}
              </span>
              <h3 className={clsxm('mt-5 text-lg font-semibold', tone.ink)}>
                {point.title}
              </h3>
              <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
                {point.body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
